import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import usePhotoStore from "@/stores/usePhotoStore";
import UploadZone from "@/components/medias/UploadZone";
import PhotoGrid from "@/components/medias/PhotoGrid";

export default function MediasPage() {
  const { id: dossierId } = useParams();
  const [expertiseId, setExpertiseId] = useState(null);
  const [categorie, setCategorie] = useState("exterieur");
  const {
    photos,
    uploading,
    error,
    fetchPhotos,
    uploadPhoto,
    updatePhoto,
    reorderPhotos,
    deletePhoto,
  } = usePhotoStore();

  // Charger l'expertise liée au dossier pour obtenir l'expertiseId
  useEffect(() => {
    async function loadExpertise() {
      try {
        const { dossiersApi } = await import("@/api");
        const dossier = await dossiersApi.getComplet(dossierId);
        if (dossier.expertise) {
          const expId = dossier.expertise.id;
          setExpertiseId(expId);
          fetchPhotos(expId);
        }
      } catch {
        // Dossier sans expertise
      }
    }
    loadExpertise();
  }, [dossierId, fetchPhotos]);

  const handleFiles = async (files) => {
    if (!expertiseId) return;
    for (const file of files) {
      await uploadPhoto(expertiseId, file, categorie);
    }
  };

  const handleUpdateLegende = (photoId, legende) => {
    updatePhoto(photoId, { legende });
  };

  const handleReorder = (items) => {
    if (!expertiseId) return;
    reorderPhotos(expertiseId, items);
  };

  if (!expertiseId) {
    return (
      <div className="p-6">
        <p className="text-slate-500">
          Ce dossier n'a pas encore d'expertise.{" "}
          <Link to={`/dossiers/${dossierId}/expertise`} className="text-carex-600 hover:underline">
            Créer une expertise
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Photos du rapport</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les photos qui seront intégrées dans le rapport d'expertise
          </p>
        </div>
        <Link
          to={`/dossiers/${dossierId}`}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Retour au dossier
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3">
          {error}
        </div>
      )}

      {/* Category selector for upload */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-600">Catégorie pour l'upload :</span>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[
            { value: "exterieur", label: "Extérieur" },
            { value: "interieur", label: "Intérieur" },
            { value: "alentours", label: "Alentours" },
          ].map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategorie(cat.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                categorie === cat.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <UploadZone onFiles={handleFiles} uploading={uploading} />

      {/* Photo grids by category */}
      <div className="space-y-8">
        <PhotoGrid
          photos={photos}
          categorie="exterieur"
          label="Vues extérieures"
          onUpdateLegende={handleUpdateLegende}
          onRemove={deletePhoto}
          onReorder={handleReorder}
        />
        <PhotoGrid
          photos={photos}
          categorie="interieur"
          label="Vues intérieures"
          onUpdateLegende={handleUpdateLegende}
          onRemove={deletePhoto}
          onReorder={handleReorder}
        />
        <PhotoGrid
          photos={photos}
          categorie="alentours"
          label="Alentours"
          onUpdateLegende={handleUpdateLegende}
          onRemove={deletePhoto}
          onReorder={handleReorder}
        />
      </div>

      {photos.length === 0 && !uploading && (
        <p className="text-center text-slate-400 py-8">
          Aucune photo pour le moment. Utilisez la zone ci-dessus pour en ajouter.
        </p>
      )}
    </div>
  );
}
